import React, { ChangeEvent } from 'react'
import { createElement, useEffect, useState } from 'react'
import {
	InputOpeningHour,
	IOpeningHours,
	IRetail,
	IRetailCreateDTO,
	IRetailForm,
	TRetailUpdateDTO,
} from 'shared/interfaces/retail'
import {
	createRetail,
	deleteRetail,
	editRetail,
	fetchAllRetail,
	getHigaEcommerce,
} from 'shared/services/retail.service'
import { EOpeningHourType, IViewProps } from './types'
import ListRetail from './view'
import cogoDefaultOptions from '../../shared/util/toaster'
import cogoToast from 'cogo-toast'
import TrashIcon from '../../assets/images/trash.svg'
import SettingsIcon from '../../assets/images/settings.svg'
import { TableRetailContent } from 'modules/ListUser/styles'
import { IUnit, IUnitOpeningHours } from 'shared/interfaces/units'
import Switch from 'shared/components/Switch'
import { unitOpenHours } from 'shared/util/Consts'
import { formatPhone } from 'shared/util/mask'

function ListRetailContainer(): JSX.Element {
	const [tableRows, setTableRows] = useState<any[]>([])
	const [loading, setLoading] = useState(false)
	const [externalIDloading, setExternalIDLoading] = useState(false)
	const [showModal, setShowModal] = useState(false)
	const [deleteModal, setDeleteModal] = useState(false)
	const [editOfferModal, setEditOfferModal] = useState(false)
	const INITIAL_STATE_RETAIL = {
		_id: '',
		name: '',
		phone: '',
		maxOccupancy: 0,
		isOffersActive: false,
		address: {
			street: '',
			number: '',
			city: '',
			state: '',
		},
		openingHours: {
			bussinessDay: {
				start: '',
				end: '',
			},
			holyDay: {
				start: '',
				end: '',
			},
		},
		internalApiReference: '',
		externalApiReference: '',
	}
	const [selectedRetail, setSelectedRetail] =
		useState<IRetailForm>(INITIAL_STATE_RETAIL)
	const [isFormValid, setIsFormValid] = useState(false)

	useEffect(() => {
		const {
			name,
			maxOccupancy,
			externalApiReference,
			phone,
			address,
			openingHours,
		} = selectedRetail

		const isNameValid = name.length >= 1
		const isMaxOccupancyValid = maxOccupancy >= 1
		const isPhoneValid = phone.length >= 14
		const isAddressValid =
			address.street.length >= 1 &&
			address.number.length >= 1 &&
			address.city.length >= 1 &&
			address.state.length >= 1
		const isOpeningHoursValid =
			openingHours.bussinessDay.start.length >= 1 &&
			openingHours.bussinessDay.end.length >= 1 &&
			openingHours.holyDay.start.length >= 1 &&
			openingHours.holyDay.end.length >= 1

		const result =
			isNameValid &&
			isMaxOccupancyValid &&
			isPhoneValid &&
			isAddressValid &&
			isOpeningHoursValid

		setIsFormValid(result)
	}, [selectedRetail])

	function getRetailAddress(address: string) {
		const separateAddress = address.split(',')

		return {
			street: separateAddress[0].trim(),
			number: separateAddress[1].trim(),
			city: separateAddress[2].trim(),
			state: separateAddress[3].trim(),
		}
	}

	function transformOpeningHours(input: {
		openingHours: IUnitOpeningHours[]
	}): {
		openingHours: IOpeningHours
	} {
		const result: IOpeningHours = {
			bussinessDay: { start: '', end: '' },
			holyDay: { start: '', end: '' },
		}

		input.openingHours.forEach((entry) => {
			const [start, end] = entry.hoursLabel.split(' às ').map((h) => h.trim())

			if (entry.daysLabel.toLowerCase().includes('segunda')) {
				result.bussinessDay = { start, end }
			} else {
				result.holyDay = { start, end }
			}
		})

		return { openingHours: result }
	}

	async function fetchAll() {
		try {
			setLoading(true)
			const retails: IUnit[] = await fetchAllRetail()
			setTableRows(
				retails.map((retail) => {
					const address = getRetailAddress(retail.info.address)
					return {
						name: handleRetailName(retail.info.name),
						city: address.city,
						state: address.state,
						phone: retail.info.phone,
						maxOccupancy: retail.maxOccupancy,
						isOffersActive: renderSwitch(retail.isOffersActive, retail),
						settings: handleSettingsIcon(retail),
						trash: handleTrashIcon(retail),
					}
				}),
			)
		} catch (error) {
		} finally {
			setLoading(false)
		}
	}

	function renderSwitch(isOffersActive: boolean, selectedRetailProp: IUnit) {
		return selectedRetailProp.externalApiReference !== 'TO_BE_DEFINED' ? (
			<Switch
				label=''
				isChecked={isOffersActive}
				onChange={() => handleSwichChange(selectedRetailProp)}
			/>
		) : (
			<Switch
				label=''
				isChecked={false}
				checked={false}
				onChange={setMissingExternalApiReferenceToast}
			/>
		)
	}

	function changeModalState(selectedRetail?: IUnit): void {
		setShowModal(true)
		if (selectedRetail) {
			const address = getRetailAddress(selectedRetail.info.address)
			const openingHoursTransformed = transformOpeningHours({
				openingHours: selectedRetail.info.openingHours,
			})

			setSelectedRetail({
				_id: String(selectedRetail.id),
				name: selectedRetail.info.name,
				phone: selectedRetail.info.phone,
				address: {
					city: address.city,
					number: address.number,
					street: address.street,
					state: address.state,
				},
				isOffersActive: selectedRetail.isOffersActive,
				maxOccupancy: selectedRetail.maxOccupancy,
				openingHours: openingHoursTransformed.openingHours,
				internalApiReference: selectedRetail.internalApiReference,
				externalApiReference:
					selectedRetail.externalApiReference === 'TO_BE_DEFINED'
						? ''
						: selectedRetail.externalApiReference,
			})
		} else {
			setSelectedRetail(INITIAL_STATE_RETAIL)
		}
	}

	function changeDeleteModalState(selectedRetail?: IUnit): void {
		setDeleteModal(!deleteModal)
		if (selectedRetail) {
			const address = getRetailAddress(selectedRetail.info.address)
			const openingHoursTransformed = transformOpeningHours({
				openingHours: selectedRetail.info.openingHours,
			})
			setSelectedRetail({
				_id: String(selectedRetail.id),
				name: selectedRetail.info.name,
				phone: selectedRetail.info.phone,
				address: {
					city: address.city,
					number: address.number,
					street: address.street,
					state: address.state,
				},
				isOffersActive: selectedRetail.isOffersActive,
				maxOccupancy: selectedRetail.maxOccupancy,
				internalApiReference: selectedRetail.internalApiReference,
				externalApiReference: selectedRetail.externalApiReference,
				openingHours: openingHoursTransformed.openingHours,
			})
		} else {
			setSelectedRetail(INITIAL_STATE_RETAIL)
		}
	}

	async function deleteRetails() {
		try {
			await deleteRetail(selectedRetail.internalApiReference)
			changeDeleteModalState()
			cogoToast.success('Loja removida com sucesso!', cogoDefaultOptions)
		} catch (error) {
		} finally {
		}
	}

	async function handleChange({
		event,
		isAddress,
	}: {
		event: ChangeEvent<HTMLInputElement>
		isAddress?: boolean
	}) {
		const { id, value } = event.target

		if (!isAddress) {
			setSelectedRetail({
				...selectedRetail,
				[id]: id === 'phone' ? formatPhone(value) : value,
			})
		} else {
			setSelectedRetail({
				...selectedRetail,
				address: {
					...selectedRetail.address,
					[id]: value,
				},
			})
		}
	}

	function getHigaEcommerceData() {
		;(async () => {
			setExternalIDLoading(true)
			try {
				if (selectedRetail.externalApiReference.length < 36) return

				const higaEcommerce = await getHigaEcommerce(
					selectedRetail.externalApiReference,
				)

				cogoToast.success(higaEcommerce.message, cogoDefaultOptions)
			} catch (error) {
				console.log('Error da chamada: ', error)
			} finally {
				setExternalIDLoading(false)
			}
		})()
	}
	useEffect(getHigaEcommerceData, [selectedRetail.externalApiReference])

	function setMissingExternalApiReferenceToast() {
		cogoToast.error(
			'Para ativar ofertas, configure primeiro o ID da loja',
			cogoDefaultOptions,
		)
	}

	function handleSelectChange(
		type: EOpeningHourType,
		position: 'start' | 'end',
		valueHour: string,
	) {
		setSelectedRetail({
			...selectedRetail,
			openingHours: {
				...selectedRetail.openingHours,
				[type]: {
					...selectedRetail.openingHours[type],
					[position]: valueHour,
				},
			},
		})
	}

	function handleOfferChange(switchState: boolean) {
		if (!selectedRetail.externalApiReference) {
			cogoToast.error(
				'Insira o ID da API externa para ativar ofertas!',
				cogoDefaultOptions,
			)

			return
		}

		setSelectedRetail((prev) => ({
			...prev,
			isOffersActive: switchState,
		}))
	}

	function returnSelectedOptions(hour: string) {
		return hour
			? unitOpenHours.find((item) => item.value === hour)
			: { value: '', label: '--:--' }
	}

	function onCloseModalOffer() {
		setEditOfferModal(false)
	}

	function onCloseModal() {
		setShowModal(false)
	}

	function handleSwichChange(selectedRetail?: IUnit) {
		setEditOfferModal(true)
		if (selectedRetail) {
			const address = getRetailAddress(selectedRetail.info.address)
			const openingHoursTransformed = transformOpeningHours({
				openingHours: selectedRetail.info.openingHours,
			})
			setSelectedRetail({
				_id: String(selectedRetail.id),
				name: selectedRetail.info.name,
				phone: selectedRetail.info.phone,
				address: {
					city: address.city,
					number: address.number,
					street: address.street,
					state: address.state,
				},
				isOffersActive: selectedRetail.isOffersActive,
				maxOccupancy: selectedRetail.maxOccupancy,
				internalApiReference: selectedRetail.internalApiReference,
				externalApiReference: selectedRetail.externalApiReference,
				openingHours: openingHoursTransformed.openingHours,
			})
		} else {
			setSelectedRetail(INITIAL_STATE_RETAIL)
		}
	}

	function handleTrashIcon(selectedRetail: IUnit): JSX.Element {
		return (
			<span
				onClick={() => {
					changeDeleteModalState(selectedRetail)
				}}
				className='trash-icon'
			>
				<img src={TrashIcon} alt='trash' />
			</span>
		)
	}

	function handleSettingsIcon(selectedRetail: IUnit): JSX.Element {
		return (
			<span
				onClick={() => {
					changeModalState(selectedRetail)
				}}
				className='settings-icon'
			>
				<img src={SettingsIcon} alt='settings' />
			</span>
		)
	}

	function handleRetailName(retailName: string): JSX.Element {
		return <TableRetailContent>{retailName}</TableRetailContent>
	}

	async function create() {
		try {
			const payload: IRetailCreateDTO = {
				...selectedRetail,
				occupancyCounter: {
					maxOccupancy: selectedRetail.maxOccupancy,
					count: 0,
				},
			}

			await createRetail(payload)

			onCloseModal()
			fetchAll()

			cogoToast.success('Loja cadastrada com sucesso!', cogoDefaultOptions)
		} catch (error) {
		} finally {
		}
	}

	async function editOffers() {
		try {
			const payload: TRetailUpdateDTO = {
				...selectedRetail,
				isOffersActive: !selectedRetail.isOffersActive,
				occupancyCounter: {
					maxOccupancy: selectedRetail.maxOccupancy,
					count: 0,
				},
			}

			await editRetail(selectedRetail.internalApiReference, payload)

			onCloseModalOffer()
			fetchAll()
			cogoToast.success('Oferta atualizada com sucesso!', cogoDefaultOptions)
		} catch (error) {}
	}

	async function edit() {
		try {
			const { internalApiReference, _id, ...restSelectedRetail } =
				selectedRetail

			const payload: TRetailUpdateDTO = {
				...restSelectedRetail,
				externalApiReference:
					selectedRetail.externalApiReference === ''
						? 'TO_BE_DEFINED'
						: selectedRetail.externalApiReference,
				occupancyCounter: {
					maxOccupancy: selectedRetail.maxOccupancy,
					count: 0,
				},
			}

			await editRetail(selectedRetail.internalApiReference, payload)

			onCloseModal()
			fetchAll()
			cogoToast.success('Loja atualizada com sucesso!', cogoDefaultOptions)
		} catch (error) {}
	}

	const tableColumns: Object[] = [
		{ Header: 'Nome', accessor: 'name', sortType: 'basic' },
		{ Header: 'Cidade', accessor: 'city', sortType: 'basic' },
		{ Header: 'Estado', accessor: 'state', sortType: 'basic' },
		{ Header: 'Telefone', accessor: 'phone', sortType: 'basic' },
		{
			Header: 'Ocupação Máx.',
			accessor: 'maxOccupancy',
			sortType: 'basic',
		},
		{ Header: 'Ofertas', accessor: 'isOffersActive', sortType: 'basic' },
		{ Header: '', accessor: 'settings', disableSortBy: true },
		{ Header: '', accessor: 'trash', disableSortBy: true },
	]

	useEffect(() => {
		;(async () => {
			fetchAll()
		})()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (!deleteModal) {
			fetchAll()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deleteModal])

	useEffect(() => {
		if (!editOfferModal) {
			fetchAll()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editOfferModal])

	useEffect(() => {
		if (!showModal) {
			fetchAll()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showModal])

	const viewProps: IViewProps = {
		tableColumns,
		tableRows,
		loading,
		showModal,
		changeModalState,
		handleChange,
		selectedRetail,
		create,
		edit,
		deleteModal,
		changeDeleteModalState,
		deleteRetails,
		isFormValid,
		handleSelectChange,
		returnSelectedOptions,
		handleOfferChange,
		editOfferModal,
		onCloseModalOffer,
		editOffers,
		onCloseModal,
		externalIDloading,
		setMissingExternalApiReferenceToast,
	}
	return createElement(ListRetail, viewProps)
}

export default ListRetailContainer
